import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Search, Download, Receipt, Package, ArrowUpDown, Boxes, ChevronLeft } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, useWorkspace } from "@/lib/store";
import { exportSalesHistory } from "@/lib/shopApi";
import { useLocalization } from "@/lib/localization";
import { formatOperatingCurrency } from "@/lib/money";
import { toast } from "sonner";
import { useTutorial } from "@/features/tutorial/TutorialContext";

function formatCurrencyLocal(value, locale) {
  return formatOperatingCurrency(value, locale);
}

function formatTimeLocal(value, locale) {
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatDateTimeLocal(value, locale) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatRelativeLocal(value, locale) {
  const target = new Date(value);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMs / 3600000);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffMs / 86400000);
  return rtf.format(diffDays, "day");
}

function isPaidStatus(status) {
  return status === "Paid";
}

function isPartialStatus(status) {
  return status === "Partly paid";
}

function isUnpaidStatus(status) {
  return status === "Unpaid";
}

export default function History() {
  const tutorial = useTutorial();
  const PAGE_SIZE = 40;
  const { session } = useAuth();
  const { sales } = useWorkspace();
  const { locale, messages } = useLocalization();
  const copy = messages.history;
  const navigate = useNavigate();
  const { saleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [range, setRange] = useState(searchParams.get("range") || "30d");
  const [paymentStatus, setPaymentStatus] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const searchSuffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const isTutorialHistory = tutorial.active && tutorial.progress?.currentStep === "transaction_history";

  const filtered = useMemo(() => {
    const start = getRangeStart(range);
    return sales.filter((s) => {
      const createdAt = new Date(s.createdAt);
      if (start && createdAt < start) return false;
      if (paymentStatus !== "all" && s.paymentStatus !== paymentStatus) return false;
      if (!query) return true;
      return (
      s.items.some((i) => i.name.toLowerCase().includes(query.toLowerCase())) ||
      s.cashier.toLowerCase().includes(query.toLowerCase())
      );
    });
  }, [paymentStatus, query, range, sales]);

  const totals = useMemo(() => ({
    revenue: filtered.reduce((a, s) => a + s.total, 0),
    count: filtered.length,
    owed: filtered.reduce((a, s) => a + s.balanceDue, 0),
  }), [filtered]);
  const counts = useMemo(() => ({
    all: sales.length,
    Paid: sales.filter((sale) => isPaidStatus(sale.paymentStatus)).length,
    "Partly paid": sales.filter((sale) => isPartialStatus(sale.paymentStatus)).length,
    Unpaid: sales.filter((sale) => isUnpaidStatus(sale.paymentStatus)).length,
  }), [sales]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedSales = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);
  const selectedSale = useMemo(() => (
    saleId ? sales.find((sale) => sale.id === saleId) || null : null
  ), [saleId, sales]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (query) nextParams.set("q", query);
    if (range !== "30d") nextParams.set("range", range);
    if (paymentStatus !== "all") nextParams.set("status", paymentStatus);
    setSearchParams(nextParams, { replace: true });
  }, [paymentStatus, query, range, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [paymentStatus, query, range]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  if (selectedSale) {
    return (
      <SaleDetailPage
        copy={copy}
        locale={locale}
        sale={selectedSale}
        onBack={() => navigate(`/history${searchSuffix}`)}
      />
    );
  }

  const exportHistory = async () => {
    if (!session?.sessionId) return;
    try {
      const start = getRangeStart(range);
      const csv = await exportSalesHistory(session.sessionId, {
        from: start ? start.toISOString() : "",
        paymentStatus,
      });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `shopbook-sales-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(copy.exportDownloaded);
    } catch (error) {
      toast.error(copy.exportFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    }
  };

  return (
    <PageContainer>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{copy.historyTitle}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{copy.countTransactions(totals.count, formatCurrencyLocal(totals.revenue, locale))}</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={range} onValueChange={setRange}>
            <TabsList>
              <TabsTrigger value="today">{copy.today}</TabsTrigger>
              <TabsTrigger value="7d">{copy.range7d}</TabsTrigger>
              <TabsTrigger value="30d">{copy.range30d}</TabsTrigger>
              <TabsTrigger value="all">{copy.all}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="sm" className="h-9" data-testid="history-export" onClick={exportHistory}>
            <Download className="h-4 w-4 mr-1.5" /> {copy.export}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <HistoryFilterPill active={paymentStatus === "all"} onClick={() => setPaymentStatus("all")}>
          {copy.all} <span className="font-mono ml-1">{counts.all}</span>
        </HistoryFilterPill>
        <HistoryFilterPill active={paymentStatus === "Paid"} onClick={() => setPaymentStatus("Paid")} tone="success">
          {copy.paid} <span className="font-mono ml-1">{counts.Paid}</span>
        </HistoryFilterPill>
        <HistoryFilterPill active={paymentStatus === "Partly paid"} onClick={() => setPaymentStatus("Partly paid")} tone="warning">
          {copy.partial} <span className="font-mono ml-1">{counts["Partly paid"]}</span>
        </HistoryFilterPill>
        <HistoryFilterPill active={paymentStatus === "Unpaid"} onClick={() => setPaymentStatus("Unpaid")} tone="danger">
          {copy.unpaid} <span className="font-mono ml-1">{counts.Unpaid}</span>
        </HistoryFilterPill>
      </div>

      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="history-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={copy.searchPlaceholder} className="pl-10 h-10" />
        </div>
      </div>

      <div className="luxe-card rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[minmax(0,1.5fr)_120px_110px_100px_130px_160px] items-center px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground border-b border-border/70 bg-muted/25">
          <div className="flex items-center gap-1.5">{copy.sale} <ArrowUpDown className="h-3 w-3 opacity-50" /></div>
          <div>{copy.cashier}</div>
          <div className="text-right">{copy.status}</div>
          <div className="text-right">{copy.sync}</div>
          <div className="text-right">{copy.amount}</div>
          <div className="text-right">{copy.action}</div>
        </div>
        <div className="divide-y divide-border/60">
          {paginatedSales.map((sale) => (
            <div
              key={sale.id}
              data-testid={`history-row-${sale.id}`}
              data-tutorial-sale={isTutorialHistory && (
                !tutorial.focus.saleId || tutorial.focus.saleId === sale.id ||
                (!sales.some((item) => item.id === tutorial.focus.saleId) && paginatedSales[0]?.id === sale.id)
              ) ? "true" : undefined}
              onClick={() => navigate(`/history/${sale.id}${searchSuffix}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(`/history/${sale.id}${searchSuffix}`);
                }
              }}
              className="grid cursor-pointer grid-cols-[minmax(0,1.5fr)_120px_110px_100px_130px_160px] items-center px-5 py-3.5 transition-colors hover:bg-accent/40"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="h-9 w-9 shrink-0 rounded-md bg-muted/60 flex items-center justify-center">
                  <Receipt className="h-4 w-4 text-muted-foreground/70" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {sale.items.map((item) => `${item.quantity}× ${item.name}`).join(", ")}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {formatRelativeLocal(sale.createdAt, locale)} · {formatTimeLocal(sale.createdAt, locale)} · <span className="font-mono">{sale.id}</span>
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">{sale.cashier}</div>
              <div className="text-right">
                <PaymentBadge copy={copy} status={sale.paymentStatus} />
              </div>
              <div className="text-right">
                <SyncBadge copy={copy} state={sale.syncState} />
              </div>
              <div className="text-right">
                <div className="font-mono text-sm font-semibold tabular-nums">{formatCurrencyLocal(sale.total, locale)}</div>
                {sale.balanceDue > 0 ? (
                  <div className="text-[10px] text-warning font-mono tabular-nums">{formatCurrencyLocal(sale.balanceDue, locale)} {copy.balanceDue}</div>
                ) : null}
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 rounded-full border-primary/15 bg-primary/5 px-4 text-primary shadow-sm transition-colors hover:border-primary/30 hover:bg-primary/10"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/history/${sale.id}${searchSuffix}`);
                  }}
                >
                  {copy.viewDetails}
                </Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div
              className="px-5 py-16 text-center"
              data-tutorial-history-empty={isTutorialHistory ? "true" : undefined}
            >
              <Boxes className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm text-muted-foreground">{copy.noTransactionsMatch}</div>
              {isTutorialHistory ? (
                <div className="mx-auto mt-2 max-w-md text-xs leading-5 text-muted-foreground">
                  A newly recorded sale may take a moment to synchronize. It will appear here automatically.
                </div>
              ) : null}
            </div>
          )}
        </div>
        {filtered.length > 0 && (
          <div className="flex items-center justify-between border-t border-border/70 bg-muted/10 px-5 py-3">
            <div className="text-sm text-muted-foreground">
              {copy.showing(
                Math.min((page - 1) * PAGE_SIZE + 1, filtered.length),
                Math.min(page * PAGE_SIZE, filtered.length),
                filtered.length
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                disabled={page === 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                {copy.previous}
              </Button>
              <div className="min-w-[68px] text-center text-sm font-medium text-foreground">
                {page} / {totalPages}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                disabled={page === totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                {copy.next}
              </Button>
            </div>
          </div>
        )}
      </div>

    </PageContainer>
  );
}

function getRangeStart(range) {
  const now = new Date();
  if (range === "all") return null;
  if (range === "today") {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  const days = range === "7d" ? 7 : 30;
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return start;
}

function PaymentBadge({ copy, status }) {
  const map = {
    Paid: { className: "bg-success/15 text-success", label: copy.paid },
    "Partly paid": { className: "bg-warning/15 text-warning", label: copy.partial },
    Unpaid: { className: "bg-destructive/15 text-destructive", label: copy.unpaid },
  };
  const current = map[status] || { className: "bg-muted text-muted-foreground", label: status };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${current.className}`}>{current.label}</span>;
}

function HistoryFilterPill({ active, onClick, children, tone = "default" }) {
  const toneClass = active
    ? tone === "success" ? "border-success bg-success/10 text-success"
    : tone === "warning" ? "border-warning bg-warning/10 text-warning"
    : tone === "danger" ? "border-destructive bg-destructive/10 text-destructive"
    : "border-primary bg-primary/10 text-primary"
    : "border-border bg-card text-foreground hover:border-foreground/30";
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${toneClass}`}
    >
      {children}
    </button>
  );
}

function SyncBadge({ copy, state }) {
  const isSynced = state === "Synced";
  return (
    <span className={`hidden md:inline text-[10px] font-medium ${isSynced ? "text-muted-foreground" : "text-warning"}`}>
      {isSynced ? copy.synced : copy.pending}
    </span>
  );
}

function getLocalizedPaymentStatus(copy, status) {
  const map = {
    Paid: copy.paid,
    "Partly paid": copy.partial,
    Unpaid: copy.unpaid,
  };
  return map[status] || status;
}

function getLocalizedSyncState(copy, state) {
  return state === "Synced" ? copy.synced : copy.pending;
}

function NestedPageShell({ backLabel, children, onBack }) {
  return (
    <PageContainer className="!max-w-none">
      <div className="grid min-h-[calc(100dvh-190px)] content-center py-8">
        <div className="mx-auto w-full max-w-4xl">
          <button
            type="button"
            onClick={onBack}
            className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            {backLabel}
          </button>
          {children}
        </div>
      </div>
    </PageContainer>
  );
}

function SaleDetailPage({ copy, locale, onBack, sale }) {
  return (
    <NestedPageShell backLabel={copy.backToHistory} onBack={onBack}>
      <div className="overflow-hidden rounded-lg border border-border/70 bg-card shadow-[0_24px_70px_-48px_rgba(15,23,42,0.5)]">
        <div className="border-b border-border/60 px-6 py-5">
          <h1 className="text-2xl font-semibold tracking-tight">{copy.saleDetail}</h1>
          <p className="mt-2 font-mono text-xs text-muted-foreground">
            {sale.id} · {formatDateTimeLocal(sale.createdAt, locale)}
          </p>
        </div>

        <div className="grid gap-5 px-6 py-6">
          <div className="grid grid-cols-2 gap-3">
            <Stat label={copy.cashier} value={sale.cashier} />
            <Stat label={copy.status} value={getLocalizedPaymentStatus(copy, sale.paymentStatus)} />
            <Stat label={copy.sync} value={getLocalizedSyncState(copy, sale.syncState)} />
            <Stat label={copy.items} value={sale.items.length.toString()} />
          </div>

          <div className="overflow-hidden rounded-lg border border-border/70 bg-card">
            <div className="px-4 py-2 border-b border-border/70 bg-muted/25 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              {copy.lineItems}
            </div>
            <div className="divide-y divide-border/60">
              {sale.items.map((i, idx) => (
                <div key={idx} className="px-4 py-3 flex items-center gap-3">
                  <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{i.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {i.quantity} × {formatCurrencyLocal(i.unitPrice, locale)}
                    </div>
                  </div>
                  <div className="font-mono text-sm font-semibold tabular-nums">{formatCurrencyLocal(i.quantity * i.unitPrice, locale)}</div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-border bg-muted/30 space-y-1 text-sm">
              <Row l={copy.total} v={formatCurrencyLocal(sale.total, locale)} bold />
              <Row l={copy.amountPaid} v={formatCurrencyLocal(sale.amountPaid, locale)} />
              {sale.balanceDue > 0 && <Row l={copy.balanceDue} v={formatCurrencyLocal(sale.balanceDue, locale)} tone="warning" bold />}
            </div>
          </div>

          <Button variant="outline" className="w-full" data-testid="sale-print-receipt" onClick={() => printSaleReceipt(sale, locale, copy, session?.shopName)}>
            {copy.printReceipt}
          </Button>
        </div>
      </div>
    </NestedPageShell>
  );
}

function escapeReceiptHTML(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function printSaleReceipt(sale, locale, copy, shopName) {
  const receiptWindow = window.open("", "_blank", "width=420,height=640");
  if (!receiptWindow) return;
  const normalizedShopName = shopName?.trim() || "Shopbook";
  const receiptTitle = copy.receiptTitle.replace(/Shopbook/gi, normalizedShopName);
  const rows = sale.items.map((item) => `
    <tr>
      <td>${item.quantity}x ${escapeReceiptHTML(item.name)}</td>
      <td style="text-align:right">${formatCurrencyLocal(item.quantity * item.unitPrice, locale)}</td>
    </tr>
  `).join("");
  receiptWindow.document.write(`
    <html>
      <head><title>${escapeReceiptHTML(receiptTitle)} ${escapeReceiptHTML(sale.id)}</title></head>
      <body style="font-family: ui-monospace, SFMono-Regular, Menlo, monospace; padding:24px; color:#111;">
        <h2 style="margin:0 0 4px;">${escapeReceiptHTML(receiptTitle)}</h2>
        <p style="margin:0 0 16px; color:#666;">${escapeReceiptHTML(sale.id)}<br/>${formatDateTimeLocal(sale.createdAt, locale)}</p>
        <table style="width:100%; border-collapse:collapse;">${rows}</table>
        <hr/>
        <p>${copy.total} <strong style="float:right">${formatCurrencyLocal(sale.total, locale)}</strong></p>
        <p>${copy.amountPaid} <strong style="float:right">${formatCurrencyLocal(sale.amountPaid, locale)}</strong></p>
        <p>${copy.balanceDue} <strong style="float:right">${formatCurrencyLocal(sale.balanceDue, locale)}</strong></p>
      </body>
    </html>
  `);
  receiptWindow.document.close();
  receiptWindow.focus();
  receiptWindow.print();
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-1">{value}</div>
    </div>
  );
}

function Row({ l, v, bold, tone }) {
  return (
    <div className="flex justify-between">
      <span className={tone === "warning" ? "text-warning" : "text-muted-foreground"}>{l}</span>
      <span className={`font-mono tabular-nums ${bold ? "font-semibold" : ""} ${tone === "warning" ? "text-warning" : ""}`}>{v}</span>
    </div>
  );
}
